import { useEffect, useState, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { StatusPill } from "@/components/StatusPill";
import { Field, SelectField } from "@/pages/FacilitiesPage";
import { toast } from "sonner";
import {
  ArrowLeft, Pencil, Trash2, Building2, User, Stethoscope, HeartPulse,
  Save, Plus, CheckCircle2, Circle, KeyRound, FileText, Eye, EyeOff, ExternalLink, X, Sparkles, CalendarClock,
} from "lucide-react";

const APPROVAL_LABELS = { not_started: "Not started", pending: "Pending", approved: "Approved", denied: "Denied" };
const APPROVAL_TONE = { not_started: "gray", pending: "orange", approved: "green", denied: "red" };

export default function FacilityDetailPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const [f, setF] = useState(null);
  const [nps, setNps] = useState([]);
  const [mds, setMds] = useState([]);
  const [cnas, setCnas] = useState([]);
  const [issues, setIssues] = useState([]);
  const [claims, setClaims] = useState([]);
  const [editOpen, setEditOpen] = useState(false);
  const [form, setForm] = useState(null);
  const [suggesting, setSuggesting] = useState(false);

  const suggestAddress = async () => {
    setSuggesting(true);
    try {
      const { data } = await api.post(`/facilities/${id}/suggest-address`);
      if (!data?.address) {
        toast.info("AI couldn't identify this facility's address");
        return;
      }
      setForm((prev) => ({ ...prev, address: data.address, city: data.city, state: data.state, zip: data.zip }));
      toast.success(`Suggested (${data.confidence} confidence) — review then Save`);
    } catch (e) {
      toast.error("Suggest failed");
    } finally {
      setSuggesting(false);
    }
  };

  const load = useCallback(async () => {
    try {
      const [{ data: fac }, { data: n }, { data: m }, { data: c }, { data: iss }, { data: cl }] = await Promise.all([
        api.get(`/facilities/${id}`), api.get("/nps"),
        api.get("/medical_directors"), api.get("/cnas"),
        api.get(`/issues?facility_id=${id}`), api.get("/claims"),
      ]);
      setF(fac); setNps(n); setMds(m); setCnas(c); setIssues(iss);
      setClaims(cl.filter((x) => x.facility_id === id));
      setForm({
        name: fac.name || "", address: fac.address || "", city: fac.city || "", state: fac.state || "",
        zip: fac.zip || "", phone: fac.phone || "",
        np_id: fac.np_id || "", medical_director_id: fac.medical_director_id || "", cna_contact_id: fac.cna_contact_id || "",
        dor_name: fac.dor_name || "", dor_phone: fac.dor_phone || "", dor_email: fac.dor_email || "",
        np_approval_status: fac.np_approval_status || "not_started",
        start_date: fac.start_date || "", patient_count: fac.patient_count || 0,
        visits_per_month: fac.visits_per_month || 0, visits_per_week: fac.visits_per_week || 0,
        last_np_visit: fac.last_np_visit || "", next_meeting: fac.next_meeting || "",
        claims_status: fac.claims_status || "", notes: fac.notes || "",
      });
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn("[facility] load failed:", e?.message || e);
      toast.error("Could not load facility");
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  if (!f || !form) return <div className="p-10 text-[#8A847A]">Loading…</div>;

  const saveEdit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...form,
        np_id: form.np_id || null, medical_director_id: form.medical_director_id || null, cna_contact_id: form.cna_contact_id || null,
        patient_count: Number(form.patient_count) || 0,
        visits_per_month: Number(form.visits_per_month) || 0,
        visits_per_week: Number(form.visits_per_week) || 0,
      };
      await api.put(`/facilities/${id}`, payload);
      toast.success("Saved");
      setEditOpen(false); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed"); }
  };

  const removeFacility = async () => {
    if (!window.confirm(`Delete ${f.name}? This cannot be undone.`)) return;
    try { await api.delete(`/facilities/${id}`); toast.success("Removed"); nav("/facilities"); }
    catch { toast.error("Delete failed"); }
  };

  return (
    <div className="p-6 md:p-10 max-w-7xl">
      <Link to="/facilities" className="inline-flex items-center gap-1 text-sm text-[#5C5850] hover:text-[#4A6B5D]" data-testid="back-to-facilities">
        <ArrowLeft className="h-4 w-4" /> All facilities
      </Link>

      <div className="mt-4 flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.15em] text-[#8A847A] flex items-center gap-2">
            <Building2 className="h-3.5 w-3.5" /> Facility folder
          </div>
          <h1 className="heading text-3xl sm:text-4xl font-bold text-[#292723] mt-2">{f.name}</h1>
          <div className="text-sm text-[#5C5850] mt-1">
            {[f.address, f.city, f.state, f.zip].filter(Boolean).join(", ") || "No address on file"}
          </div>
          <div className="flex flex-wrap items-center gap-2 mt-3">
            <StatusPill status={f.status_light} label={f.status_light === "red" ? "Needs attention" : f.status_light === "orange" ? "Soon" : "Operating normally"} />
            <StatusPill status={APPROVAL_TONE[f.np_approval_status] || "gray"} label={`MD approval · ${APPROVAL_LABELS[f.np_approval_status] || "—"}`} />
            {f.phone && <span className="text-xs text-[#5C5850]">📞 {f.phone}</span>}
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setEditOpen(true)} className="border-[#E5E0D8]" data-testid="edit-facility-btn"><Pencil className="h-4 w-4 mr-2" />Edit</Button>
          <Button variant="outline" onClick={removeFacility} className="border-[#E5E0D8] text-[#C17D6E]" data-testid="delete-facility-btn"><Trash2 className="h-4 w-4 mr-2" />Remove</Button>
        </div>
      </div>

      {/* Bento */}
      <div className="mt-6 grid lg:grid-cols-3 gap-4">
        <ContactCard title="Assigned NP" icon={User} person={f.np} link={f.np ? `/nps/${f.np.id}` : null} empty="No NP assigned" />
        <ContactCard title="Medical Director" icon={Stethoscope} person={f.medical_director} empty="No Medical Director" />
        <ContactCard title="CNA Contact" icon={HeartPulse} person={f.cna_contact} empty="No CNA contact" />
        <InfoCard title="DOR" rows={[
          ["Name", f.dor_name || "—"], ["Phone", f.dor_phone || "—"], ["Email", f.dor_email || "—"],
        ]} />
        <InfoCard title="Operations" rows={[
          ["Start date", f.start_date || "—"],
          ["Last NP visit", f.last_np_visit || "—"],
          ["Next meeting", f.next_meeting || "—"],
          ["Claims status", f.claims_status || "—"],
        ]} />
        <InfoCard title="Metrics" rows={[
          ["Patients", String(f.patient_count || 0)],
          ["Visits / month", String(f.visits_per_month || 0)],
          ["Visits / week", String(f.visits_per_week || 0)],
          ["Open issues", String(f.open_issues_count || 0)],
        ]} />
      </div>

      {f.notes && (
        <div className="mt-6 bg-white border border-[#E5E0D8] rounded-md p-5">
          <div className="text-xs uppercase tracking-[0.06em] text-[#5C5850]">Notes</div>
          <p className="mt-2 text-sm text-[#292723] whitespace-pre-wrap">{f.notes}</p>
        </div>
      )}

      {(f.upcoming_meetings || []).length > 0 && (
        <div className="mt-6 bg-white border border-[#E5E0D8] rounded-md" data-testid="facility-meetings-card">
          <div className="p-5 border-b border-[#E5E0D8] flex items-center justify-between">
            <div>
              <div className="text-xs uppercase tracking-[0.06em] text-[#5C5850]">Calendar</div>
              <h2 className="heading text-lg font-semibold">Upcoming meetings ({f.upcoming_meetings.length})</h2>
            </div>
            <Link to="/meetings" className="text-xs text-[#4A6B5D] hover:underline">All meetings →</Link>
          </div>
          <div className="divide-y divide-[#E5E0D8]">
            {f.upcoming_meetings.map((m) => (
              <div key={m.id} className="px-5 py-3">
                <div className="text-sm font-medium text-[#292723]">{m.title}</div>
                <div className="text-xs text-[#8A847A] mt-0.5">
                  {(() => { try { return new Date(m.when).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }); } catch { return m.when; } })()}
                </div>
                {m.attendees && <div className="text-xs text-[#5C5850] mt-1">{m.attendees}</div>}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Tabs */}
      <Tabs defaultValue="followups" className="mt-8">
        <TabsList className="bg-white border border-[#E5E0D8]">
          <TabsTrigger value="followups" data-testid="tab-followups">Follow-ups</TabsTrigger>
          <TabsTrigger value="visits" data-testid="tab-visits">Visits</TabsTrigger>
          <TabsTrigger value="issues" data-testid="tab-issues">Issues ({issues.length})</TabsTrigger>
          <TabsTrigger value="claims" data-testid="tab-claims">Claims ({claims.length})</TabsTrigger>
          <TabsTrigger value="documents" data-testid="tab-documents">Documents</TabsTrigger>
          <TabsTrigger value="credentials" data-testid="tab-credentials">Credentials</TabsTrigger>
        </TabsList>

        <TabsContent value="followups" className="mt-4">
          <FollowUps facilityId={id} items={f.follow_ups || []} reload={load} />
        </TabsContent>
        <TabsContent value="visits" className="mt-4">
          <FacilityVisits facilityId={id} nps={nps} defaultNpId={f.np_id} />
        </TabsContent>
        <TabsContent value="issues" className="mt-4">
          <FacilityIssues facilityId={id} items={issues} reload={load} />
        </TabsContent>
        <TabsContent value="claims" className="mt-4">
          <FacilityClaims items={claims} />
        </TabsContent>
        <TabsContent value="documents" className="mt-4">
          <Documents facilityId={id} items={f.documents || []} reload={load} />
        </TabsContent>
        <TabsContent value="credentials" className="mt-4">
          <Credentials facilityId={id} items={f.credentials || []} reload={load} />
        </TabsContent>
      </Tabs>

      {/* Edit sheet */}
      <Sheet open={editOpen} onOpenChange={setEditOpen}>
        <SheetContent className="w-full sm:max-w-lg bg-white overflow-y-auto">
          <SheetHeader>
            <SheetTitle className="heading text-2xl">Edit facility</SheetTitle>
            <SheetDescription>Update the facility folder.</SheetDescription>
          </SheetHeader>
          <form onSubmit={saveEdit} className="mt-6 space-y-4" data-testid="edit-facility-form">
            <Field label="Name" required value={form.name} onChange={(v) => setForm({ ...form, name: v })} />
            <div>
              <div className="flex items-center justify-between">
                <Label className="text-xs uppercase tracking-[0.06em] text-[#5C5850]">Address</Label>
                <button
                  type="button"
                  onClick={suggestAddress}
                  disabled={suggesting}
                  className="text-[11px] text-[#4A6B5D] hover:underline inline-flex items-center gap-1 disabled:opacity-50"
                  data-testid="suggest-address-btn"
                >
                  <Sparkles className="h-3 w-3"/> {suggesting ? "Looking up…" : "Suggest from web"}
                </button>
              </div>
              <Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="mt-1.5 h-10 bg-white border-[#E5E0D8]" placeholder="Street address" data-testid="edit-address-input"/>
            </div>
            <div className="grid grid-cols-3 gap-2">
              <Field label="City" value={form.city} onChange={(v) => setForm({ ...form, city: v })} />
              <Field label="State" value={form.state} onChange={(v) => setForm({ ...form, state: v })} />
              <Field label="Zip" value={form.zip} onChange={(v) => setForm({ ...form, zip: v })} />
            </div>
            <Field label="Phone" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} />
            <SelectField label="Assigned NP" value={form.np_id} onChange={(v) => setForm({ ...form, np_id: v })} options={[{ value: "", label: "— none —" }, ...nps.map((n) => ({ value: n.id, label: n.name }))]} />
            <SelectField label="Medical Director" value={form.medical_director_id} onChange={(v) => setForm({ ...form, medical_director_id: v })} options={[{ value: "", label: "— none —" }, ...mds.map((m) => ({ value: m.id, label: m.name }))]} />
            <SelectField label="CNA Contact" value={form.cna_contact_id} onChange={(v) => setForm({ ...form, cna_contact_id: v })} options={[{ value: "", label: "— none —" }, ...cnas.map((c) => ({ value: c.id, label: c.name }))]} />
            <div className="grid grid-cols-3 gap-2">
              <Field label="DOR Name" value={form.dor_name} onChange={(v) => setForm({ ...form, dor_name: v })} />
              <Field label="DOR Phone" value={form.dor_phone} onChange={(v) => setForm({ ...form, dor_phone: v })} />
              <Field label="DOR Email" value={form.dor_email} onChange={(v) => setForm({ ...form, dor_email: v })} />
            </div>
            <SelectField label="NP Approval Status" value={form.np_approval_status} onChange={(v) => setForm({ ...form, np_approval_status: v })} options={Object.entries(APPROVAL_LABELS).map(([v, l]) => ({ value: v, label: l }))} />
            <div className="grid grid-cols-2 gap-2">
              <Field label="Start Date" type="date" value={form.start_date} onChange={(v) => setForm({ ...form, start_date: v })} />
              <Field label="Last NP Visit" type="date" value={form.last_np_visit} onChange={(v) => setForm({ ...form, last_np_visit: v })} />
            </div>
            <Field label="Next Meeting" type="datetime-local" value={form.next_meeting} onChange={(v) => setForm({ ...form, next_meeting: v })} />
            <div className="grid grid-cols-3 gap-2">
              <Field label="Patients" type="number" value={form.patient_count} onChange={(v) => setForm({ ...form, patient_count: v })} />
              <Field label="Visits / mo" type="number" value={form.visits_per_month} onChange={(v) => setForm({ ...form, visits_per_month: v })} />
              <Field label="Visits / wk" type="number" value={form.visits_per_week} onChange={(v) => setForm({ ...form, visits_per_week: v })} />
            </div>
            <Field label="Claims status" value={form.claims_status} onChange={(v) => setForm({ ...form, claims_status: v })} />
            <div>
              <Label className="text-xs uppercase tracking-[0.06em] text-[#5C5850]">Notes</Label>
              <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={3} className="mt-1.5 bg-white border-[#E5E0D8]" />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setEditOpen(false)}>Cancel</Button>
              <Button type="submit" className="bg-[#4A6B5D] hover:bg-[#3E594D]"><Save className="h-4 w-4 mr-2" />Save</Button>
            </div>
          </form>
        </SheetContent>
      </Sheet>
    </div>
  );
}

function ContactCard({ title, icon: Icon, person, link, empty }) {
  return (
    <div className="bg-white border border-[#E5E0D8] rounded-md p-5">
      <div className="text-xs uppercase tracking-[0.06em] text-[#5C5850] flex items-center gap-2">
        <Icon className="h-3.5 w-3.5" /> {title}
      </div>
      {person ? (
        <div className="mt-2">
          {link ? (
            <Link to={link} className="heading font-semibold text-[#292723] hover:text-[#4A6B5D]">{person.name}</Link>
          ) : (
            <div className="heading font-semibold text-[#292723]">{person.name}</div>
          )}
          <div className="text-xs text-[#5C5850] mt-1 space-y-0.5">
            {person.email && <div>{person.email}</div>}
            {person.phone && <div>{person.phone}</div>}
          </div>
        </div>
      ) : (
        <div className="mt-2 text-sm text-[#8A847A] italic">{empty}</div>
      )}
    </div>
  );
}

function InfoCard({ title, rows }) {
  return (
    <div className="bg-white border border-[#E5E0D8] rounded-md p-5">
      <div className="text-xs uppercase tracking-[0.06em] text-[#5C5850]">{title}</div>
      <div className="mt-3 space-y-2">
        {rows.map(([k, v]) => (
          <div key={k} className="flex items-center justify-between text-sm gap-3">
            <span className="text-[#8A847A]">{k}</span>
            <span className="text-[#292723] text-right truncate max-w-[60%]">{v}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function FacilityVisits({ facilityId, nps, defaultNpId }) {
  const today = new Date();
  const monthAgo = new Date(today); monthAgo.setDate(monthAgo.getDate() - 30);
  const fmt = (d) => d.toISOString().slice(0, 10);
  const [from_, setFrom] = useState(fmt(monthAgo));
  const [to, setTo] = useState(fmt(today));
  const [visits, setVisits] = useState([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [cptSummary, setCptSummary] = useState(null);
  const [form, setForm] = useState({ visit_date: fmt(today), np_id: defaultNpId || "", duration_min: "", notes: "" });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { facility_id: facilityId };
      if (from_) params.from_ = from_;
      if (to) params.to = to;
      const [visitsRes, cptRes] = await Promise.all([
        api.get("/visits", { params }),
        api.get(`/facilities/${facilityId}/cpt-summary`, { params: { from_, to } }).catch(() => ({ data: null })),
      ]);
      setVisits(visitsRes.data?.visits || []);
      setCount(visitsRes.data?.count || 0);
      setCptSummary(cptRes.data);
    } catch { toast.error("Could not load visits"); }
    finally { setLoading(false); }
  }, [facilityId, from_, to]);
  useEffect(() => { load(); }, [load]);

  const add = async (e) => {
    e.preventDefault();
    if (!form.visit_date) return;
    setSaving(true);
    try {
      await api.post("/visits", {
        facility_id: facilityId,
        np_id: form.np_id || null,
        visit_date: form.visit_date,
        duration_min: form.duration_min ? Number(form.duration_min) : null,
        notes: form.notes || "",
      });
      setForm({ visit_date: fmt(today), np_id: defaultNpId || "", duration_min: "", notes: "" });
      toast.success("Visit logged");
      load();
    } catch { toast.error("Could not log visit"); }
    finally { setSaving(false); }
  };

  const remove = async (v) => {
    if (!window.confirm("Delete this visit?")) return;
    try { await api.delete(`/visits/${v.id}`); load(); }
    catch { toast.error("Could not delete"); }
  };

  const byNp = {};
  visits.forEach((v) => {
    const k = v.np_name || "Unassigned";
    byNp[k] = (byNp[k] || 0) + 1;
  });

  return (
    <div className="space-y-4">
      <div className="bg-white border border-[#E5E0D8] rounded-md p-5">
        <div className="text-xs uppercase tracking-[0.06em] text-[#5C5850]">Date range</div>
        <div className="mt-2 grid sm:grid-cols-3 gap-3">
          <div>
            <Label className="text-[11px] text-[#8A847A]">From</Label>
            <Input type="date" value={from_} onChange={(e) => setFrom(e.target.value)} className="mt-1 h-10 bg-white border-[#E5E0D8]" data-testid="visits-from"/>
          </div>
          <div>
            <Label className="text-[11px] text-[#8A847A]">To</Label>
            <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="mt-1 h-10 bg-white border-[#E5E0D8]" data-testid="visits-to"/>
          </div>
          <div className="sm:col-span-1 flex items-end">
            <div className="w-full bg-[#F0EBE1] border border-[#E5E0D8] rounded-md p-3 text-center">
              <div className="text-[10px] uppercase tracking-[0.08em] text-[#5C5850]">Total visits</div>
              <div className="heading text-2xl font-bold text-[#292723]" data-testid="visits-total">{loading ? "…" : count}</div>
            </div>
          </div>
        </div>
        {Object.keys(byNp).length > 0 && (
          <div className="mt-3 flex flex-wrap gap-2 text-xs">
            {Object.entries(byNp).map(([name, n]) => (
              <span key={name} className="inline-flex items-center gap-1 px-2 py-1 rounded border border-[#E5E0D8] bg-[#F7F5F0] text-[#5C5850]">
                <span className="font-medium text-[#292723]">{name}</span>
                <span className="font-mono">{n}</span>
              </span>
            ))}
          </div>
        )}
      </div>

      <form onSubmit={add} className="bg-white border border-[#E5E0D8] rounded-md p-5 grid sm:grid-cols-5 gap-2 items-end">
        <div className="sm:col-span-1">
          <Label className="text-[11px] text-[#8A847A]">Date</Label>
          <Input type="date" required value={form.visit_date} onChange={(e) => setForm({ ...form, visit_date: e.target.value })} className="mt-1 h-10 bg-white border-[#E5E0D8]" data-testid="visit-form-date"/>
        </div>
        <div className="sm:col-span-2">
          <Label className="text-[11px] text-[#8A847A]">NP</Label>
          <select
            value={form.np_id}
            onChange={(e) => setForm({ ...form, np_id: e.target.value })}
            className="mt-1 h-10 w-full bg-white border border-[#E5E0D8] rounded-md px-3 text-sm"
            data-testid="visit-form-np"
          >
            <option value="">— none —</option>
            {nps.map((n) => <option key={n.id} value={n.id}>{n.name}</option>)}
          </select>
        </div>
        <div className="sm:col-span-1">
          <Label className="text-[11px] text-[#8A847A]">Duration (min)</Label>
          <Input type="number" min="0" value={form.duration_min} onChange={(e) => setForm({ ...form, duration_min: e.target.value })} className="mt-1 h-10 bg-white border-[#E5E0D8]" placeholder="30" data-testid="visit-form-duration"/>
        </div>
        <div className="sm:col-span-1">
          <Button type="submit" disabled={saving} className="w-full bg-[#4A6B5D] hover:bg-[#3E594D]" data-testid="visit-add-btn">
            <Plus className="h-4 w-4 mr-1"/> Log visit
          </Button>
        </div>
        <div className="sm:col-span-5">
          <Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Notes (optional)" className="h-10 bg-white border-[#E5E0D8]" data-testid="visit-form-notes"/>
        </div>
      </form>

      {cptSummary && (cptSummary.total_units > 0) && (
        <div className="bg-white border border-[#E5E0D8] rounded-md p-5" data-testid="cpt-summary-card">
          <div className="flex items-baseline justify-between flex-wrap gap-2">
            <div className="text-xs uppercase tracking-[0.06em] text-[#5C5850]">Top CPT codes · {from_} → {to}</div>
            <div className="flex gap-4 text-xs text-[#5C5850]">
              <span><span className="font-mono font-semibold text-[#292723]">{cptSummary.visits_with_codes}</span> visits coded</span>
              <span><span className="font-mono font-semibold text-[#292723]">{cptSummary.total_units}</span> units</span>
              <span>Est. revenue <span className="font-mono font-semibold text-[#3B5C46]">${cptSummary.total_revenue.toLocaleString(undefined,{minimumFractionDigits:2,maximumFractionDigits:2})}</span></span>
            </div>
          </div>
          <div className="mt-3 grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {cptSummary.top_codes.slice(0, 9).map((c) => (
              <div key={c.code} className="border border-[#E5E0D8] rounded-md p-2.5 bg-[#FAF8F3] flex items-center justify-between" data-testid={`cpt-top-${c.code}`}>
                <div>
                  <div className="font-mono text-sm font-semibold text-[#292723]">{c.code}</div>
                  <div className="text-[10px] uppercase tracking-[0.08em] text-[#8A847A]">{c.count} units · ${c.price.toFixed(0)}/unit</div>
                </div>
                <div className="text-sm font-mono font-semibold text-[#3B5C46]">${c.revenue.toLocaleString(undefined,{maximumFractionDigits:0})}</div>
              </div>
            ))}
          </div>
          <div className="mt-2 text-[10px] text-[#8A847A]">Prices are editable on the CPT Prices page.</div>
        </div>
      )}

      {visits.length === 0 ? (
        <div className="bg-white border border-[#E5E0D8] rounded-md p-10 text-center" data-testid="visits-empty">
          <CalendarClock className="h-8 w-8 mx-auto text-[#8A847A] opacity-60"/>
          <p className="text-sm text-[#8A847A] mt-3">No visits logged in this date range.</p>
        </div>
      ) : (
        <div className="bg-white border border-[#E5E0D8] rounded-md overflow-hidden">
          <table className="w-full">
            <thead><tr className="bg-[#F7F5F0]">
              <th className="text-left px-4 py-2 text-xs uppercase tracking-[0.05em] text-[#5C5850]">Date</th>
              <th className="text-left px-4 py-2 text-xs uppercase tracking-[0.05em] text-[#5C5850]">NP</th>
              <th className="text-left px-4 py-2 text-xs uppercase tracking-[0.05em] text-[#5C5850]">CPT</th>
              <th className="text-right px-4 py-2 text-xs uppercase tracking-[0.05em] text-[#5C5850]">Duration</th>
              <th className="text-left px-4 py-2 text-xs uppercase tracking-[0.05em] text-[#5C5850]">Notes</th>
              <th className="w-8"></th>
            </tr></thead>
            <tbody>{visits.map((v) => (
              <tr key={v.id} className="border-t border-[#E5E0D8]" data-testid={`visit-row-${v.id}`}>
                <td className="px-4 py-2 text-sm font-mono">{v.visit_date}</td>
                <td className="px-4 py-2 text-sm">{v.np_name || <span className="italic text-[#8A847A]">—</span>}</td>
                <td className="px-4 py-2 text-sm">
                  {(v.cpt_codes || []).length === 0 ? <span className="text-[#8A847A]">—</span> : (
                    <div className="flex flex-wrap gap-1">
                      {v.cpt_codes.map((c, i) => (
                        <span key={i} className="inline-flex items-center px-1.5 py-0.5 rounded border border-[#B7CDBB] bg-[#E9F0EA] text-[#3B5C46] text-[11px] font-mono" data-testid={`visit-cpt-${v.id}-${c.code}`}>
                          {c.code}{c.units > 1 ? `×${c.units}` : ""}
                        </span>
                      ))}
                    </div>
                  )}
                </td>
                <td className="px-4 py-2 text-sm text-right font-mono">{v.duration_min ? `${v.duration_min}m` : "—"}</td>
                <td className="px-4 py-2 text-sm text-[#5C5850] truncate max-w-[240px]">{v.notes || "—"}</td>
                <td className="px-2 py-2 text-right">
                  <button onClick={() => remove(v)} className="text-[#8A847A] hover:text-[#C17D6E] p-1" aria-label="Delete visit" data-testid={`visit-delete-${v.id}`}>
                    <Trash2 className="h-3.5 w-3.5"/>
                  </button>
                </td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function FollowUps({ facilityId, items, reload }) {
  const [form, setForm] = useState({ title: "", due_date: "", notes: "" });
  const [saving, setSaving] = useState(false);
  const add = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) return;
    setSaving(true);
    try {
      await api.post(`/facilities/${facilityId}/follow-ups`, { ...form, status: "pending" });
      setForm({ title: "", due_date: "", notes: "" }); reload();
    } finally { setSaving(false); }
  };
  const toggle = async (fu) => {
    await api.put(`/facilities/${facilityId}/follow-ups/${fu.id}`, {
      ...fu, status: fu.status === "done" ? "pending" : "done",
    });
    reload();
  };
  const remove = async (fu) => { await api.delete(`/facilities/${facilityId}/follow-ups/${fu.id}`); reload(); };

  return (
    <div className="space-y-4">
      <form onSubmit={add} className="bg-white border border-[#E5E0D8] rounded-md p-4 grid grid-cols-1 md:grid-cols-[1fr_160px_auto] gap-2" data-testid="add-followup-form">
        <Input placeholder="Follow-up title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="h-10 bg-white border-[#E5E0D8]" data-testid="followup-title" />
        <Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} className="h-10 bg-white border-[#E5E0D8]" data-testid="followup-date" />
        <Button type="submit" disabled={saving} className="bg-[#4A6B5D] hover:bg-[#3E594D]" data-testid="add-followup-btn"><Plus className="h-4 w-4 mr-2"/>Add</Button>
      </form>
      {items.length === 0 ? (
        <div className="text-sm text-[#8A847A] p-6 text-center bg-white border border-[#E5E0D8] rounded-md">No follow-ups.</div>
      ) : items.map((fu) => (
        <div key={fu.id} className="bg-white border border-[#E5E0D8] rounded-md p-4 flex items-start gap-3" data-testid={`followup-${fu.id}`}>
          <button onClick={() => toggle(fu)} className="mt-0.5">
            {fu.status === "done" ? <CheckCircle2 className="h-5 w-5 text-[#507A5E]"/> : <Circle className="h-5 w-5 text-[#8A847A]"/>}
          </button>
          <div className="flex-1 min-w-0">
            <div className={`font-medium ${fu.status === "done" ? "line-through text-[#8A847A]" : "text-[#292723]"}`}>{fu.title}</div>
            {fu.due_date && <div className="text-xs text-[#8A847A] mt-0.5">Due {fu.due_date}</div>}
            {fu.notes && <div className="text-sm text-[#5C5850] mt-1">{fu.notes}</div>}
          </div>
          <Button variant="ghost" size="sm" onClick={() => remove(fu)} className="text-[#C17D6E]"><Trash2 className="h-4 w-4"/></Button>
        </div>
      ))}
    </div>
  );
}

function FacilityIssues({ facilityId, items, reload }) {
  const [form, setForm] = useState({ title: "", severity: "normal", description: "" });
  const [saving, setSaving] = useState(false);
  const add = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) return;
    setSaving(true);
    try {
      await api.post("/issues", { ...form, facility_id: facilityId, status: "open" });
      setForm({ title: "", severity: "normal", description: "" }); reload();
    } finally { setSaving(false); }
  };
  const resolve = async (iss) => { await api.put(`/issues/${iss.id}`, { status: "resolved" }); reload(); };
  const remove = async (iss) => { await api.delete(`/issues/${iss.id}`); reload(); };
  return (
    <div className="space-y-4">
      <form onSubmit={add} className="bg-white border border-[#E5E0D8] rounded-md p-4 grid grid-cols-1 md:grid-cols-[1fr_160px_auto] gap-2" data-testid="add-issue-form">
        <Input placeholder="Issue title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="h-10 bg-white border-[#E5E0D8]" />
        <select value={form.severity} onChange={(e) => setForm({ ...form, severity: e.target.value })} className="h-10 rounded-md border border-[#E5E0D8] bg-white px-3 text-sm">
          <option value="low">low</option><option value="normal">normal</option><option value="high">high</option><option value="critical">critical</option>
        </select>
        <Button type="submit" disabled={saving} className="bg-[#4A6B5D] hover:bg-[#3E594D]"><Plus className="h-4 w-4 mr-2"/>Add</Button>
      </form>
      {items.length === 0 ? (
        <div className="text-sm text-[#8A847A] p-6 text-center bg-white border border-[#E5E0D8] rounded-md">No open issues.</div>
      ) : items.map((i) => (
        <div key={i.id} className="bg-white border border-[#E5E0D8] rounded-md p-4 flex items-start gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="font-medium text-[#292723]">{i.title}</span>
              <StatusPill status={i.severity === "critical" ? "red" : i.severity === "high" ? "orange" : "gray"} label={i.severity} />
            </div>
            {i.description && <div className="text-sm text-[#5C5850] mt-1">{i.description}</div>}
          </div>
          <Button variant="ghost" size="sm" onClick={() => resolve(i)}><CheckCircle2 className="h-4 w-4"/></Button>
          <Button variant="ghost" size="sm" onClick={() => remove(i)} className="text-[#C17D6E]"><Trash2 className="h-4 w-4"/></Button>
        </div>
      ))}
    </div>
  );
}

function FacilityClaims({ items }) {
  if (!items.length) return <div className="text-sm text-[#8A847A] p-6 text-center bg-white border border-[#E5E0D8] rounded-md">No claims recorded. Add them from the Claims page.</div>;
  return (
    <div className="bg-white border border-[#E5E0D8] rounded-md overflow-hidden">
      <table className="w-full">
        <thead><tr className="bg-[#F7F5F0]">
          <th className="text-left px-4 py-2 text-xs uppercase tracking-[0.05em] text-[#5C5850]">Patient</th>
          <th className="text-right px-4 py-2 text-xs uppercase tracking-[0.05em] text-[#5C5850]">Amount</th>
          <th className="text-left px-4 py-2 text-xs uppercase tracking-[0.05em] text-[#5C5850]">Submitted</th>
          <th className="text-left px-4 py-2 text-xs uppercase tracking-[0.05em] text-[#5C5850]">Status</th>
        </tr></thead>
        <tbody>{items.map((c) => (
          <tr key={c.id} className="border-t border-[#E5E0D8]">
            <td className="px-4 py-2 text-sm">{c.patient_name || "—"}</td>
            <td className="px-4 py-2 text-sm text-right font-mono">${(c.amount || 0).toFixed(2)}</td>
            <td className="px-4 py-2 text-sm">{c.submitted_date || "—"}</td>
            <td className="px-4 py-2"><StatusPill status={c.status_light} label={c.status} /></td>
          </tr>
        ))}</tbody>
      </table>
    </div>
  );
}

function Documents({ facilityId, items, reload }) {
  const [form, setForm] = useState({ name: "", url: "", kind: "" });
  const add = async (e) => {
    e.preventDefault();
    if (!form.name || !form.url) return;
    await api.post(`/facilities/${facilityId}/documents`, form);
    setForm({ name: "", url: "", kind: "" }); reload();
  };
  const remove = async (d) => { await api.delete(`/facilities/${facilityId}/documents/${d.id}`); reload(); };
  return (
    <div className="space-y-4">
      <form onSubmit={add} className="bg-white border border-[#E5E0D8] rounded-md p-4 grid grid-cols-1 md:grid-cols-[1fr_1fr_160px_auto] gap-2">
        <Input placeholder="Document name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="h-10 bg-white border-[#E5E0D8]" />
        <Input placeholder="URL" value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} className="h-10 bg-white border-[#E5E0D8]" />
        <Input placeholder="Kind (contract, W9…)" value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })} className="h-10 bg-white border-[#E5E0D8]" />
        <Button type="submit" className="bg-[#4A6B5D] hover:bg-[#3E594D]"><Plus className="h-4 w-4 mr-2"/>Add</Button>
      </form>
      {items.length === 0 ? (
        <div className="text-sm text-[#8A847A] p-6 text-center bg-white border border-[#E5E0D8] rounded-md">No documents linked yet.</div>
      ) : items.map((d) => (
        <div key={d.id} className="bg-white border border-[#E5E0D8] rounded-md p-4 flex items-center gap-3">
          <FileText className="h-4 w-4 text-[#4A6B5D]"/>
          <div className="flex-1 min-w-0">
            <a href={d.url} target="_blank" rel="noreferrer" className="font-medium text-[#292723] hover:text-[#4A6B5D] inline-flex items-center gap-1">
              {d.name} <ExternalLink className="h-3 w-3"/>
            </a>
            {d.kind && <span className="ml-2 text-xs text-[#8A847A]">{d.kind}</span>}
          </div>
          <Button variant="ghost" size="sm" onClick={() => remove(d)} className="text-[#C17D6E]"><Trash2 className="h-4 w-4"/></Button>
        </div>
      ))}
    </div>
  );
}

function Credentials({ facilityId, items, reload }) {
  const [form, setForm] = useState({ label: "", username: "", password: "", url: "", notes: "" });
  const [revealed, setRevealed] = useState({}); // id -> password
  const add = async (e) => {
    e.preventDefault();
    if (!form.label) return;
    await api.post(`/facilities/${facilityId}/credentials`, form);
    setForm({ label: "", username: "", password: "", url: "", notes: "" }); reload();
  };
  const reveal = async (c) => {
    if (revealed[c.id]) {
      setRevealed({ ...revealed, [c.id]: null });
      return;
    }
    try {
      const { data } = await api.get(`/facilities/${facilityId}/credentials/${c.id}/reveal`);
      setRevealed({ ...revealed, [c.id]: data.password });
    } catch { toast.error("Reveal failed"); }
  };
  const remove = async (c) => { await api.delete(`/facilities/${facilityId}/credentials/${c.id}`); reload(); };
  return (
    <div className="space-y-4">
      <div className="text-xs text-[#8A847A] flex items-center gap-1.5">
        <KeyRound className="h-3.5 w-3.5"/> Passwords are encrypted at rest with AES; only visible when you tap Reveal.
      </div>
      <form onSubmit={add} className="bg-white border border-[#E5E0D8] rounded-md p-4 grid grid-cols-1 md:grid-cols-2 gap-2" data-testid="add-cred-form">
        <Input placeholder="Label (portal name)" value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} className="h-10 bg-white border-[#E5E0D8]" data-testid="cred-label" />
        <Input placeholder="URL (optional)" value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} className="h-10 bg-white border-[#E5E0D8]" />
        <Input placeholder="Username" value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} className="h-10 bg-white border-[#E5E0D8]" data-testid="cred-username" />
        <Input placeholder="Password" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="h-10 bg-white border-[#E5E0D8]" data-testid="cred-password" />
        <Textarea placeholder="Notes (recovery hints, MFA…)" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} className="bg-white border-[#E5E0D8] md:col-span-2" />
        <div className="md:col-span-2 flex justify-end"><Button type="submit" className="bg-[#4A6B5D] hover:bg-[#3E594D]"><Plus className="h-4 w-4 mr-2"/>Add credential</Button></div>
      </form>
      {items.length === 0 ? (
        <div className="text-sm text-[#8A847A] p-6 text-center bg-white border border-[#E5E0D8] rounded-md">No credentials stored yet.</div>
      ) : items.map((c) => (
        <div key={c.id} className="bg-white border border-[#E5E0D8] rounded-md p-4" data-testid={`cred-${c.id}`}>
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="font-medium text-[#292723] flex items-center gap-2">
                <KeyRound className="h-4 w-4 text-[#4A6B5D]"/> {c.label}
                {c.url && <a href={c.url} target="_blank" rel="noreferrer" className="text-xs text-[#4A6B5D] inline-flex items-center gap-0.5"><ExternalLink className="h-3 w-3"/></a>}
              </div>
              <div className="text-sm text-[#5C5850] mt-1 font-mono">{c.username || "—"}</div>
              <div className="text-sm text-[#292723] mt-1 font-mono flex items-center gap-2">
                {revealed[c.id] ? revealed[c.id] : (c.has_password ? "•••••••••" : "—")}
                {c.has_password && (
                  <Button variant="ghost" size="sm" onClick={() => reveal(c)} data-testid={`reveal-${c.id}`}>
                    {revealed[c.id] ? <EyeOff className="h-4 w-4"/> : <Eye className="h-4 w-4"/>}
                  </Button>
                )}
              </div>
              {c.notes && <div className="text-xs text-[#5C5850] mt-2">{c.notes}</div>}
            </div>
            <Button variant="ghost" size="sm" onClick={() => remove(c)} className="text-[#C17D6E]"><Trash2 className="h-4 w-4"/></Button>
          </div>
        </div>
      ))}
    </div>
  );
}
